'use client'

import { useState, useEffect } from 'react'

export interface UserLocation {
    countryCode: string
    countryName: string
    city?: string
    isLoading: boolean
    error?: string
}

/**
 * Hook to detect user's location based on IP address
 * Uses multiple fallback services for reliability
 *
 * @returns UserLocation object with country code and loading state
 *
 * @example
 * const { countryCode, isLoading } = useUserLocation()
 * // countryCode will be 'IN' for India, 'US' for USA, etc.
 */
export function useUserLocation(): UserLocation {
    const [location, setLocation] = useState<UserLocation>({
        countryCode: 'US', // Default fallback
        countryName: 'United States',
        isLoading: true
    })

    useEffect(() => {
        const detectLocation = async () => {
            try {
                // First, check if location is cached in localStorage
                const cachedLocation = getCachedLocation()
                if (cachedLocation) {
                    setLocation({
                        ...cachedLocation,
                        isLoading: false
                    })
                    return
                }

                // Option 1: Try ipapi.co (free, 30,000 requests/month, no API key)
                let locationData = await fetchFromIpApi()

                // Option 2: Fallback to ip-api.com (free, no API key, rate limited)
                if (!locationData) {
                    locationData = await fetchFromIpApiCom()
                }

                // Option 3: Fallback to CloudFlare trace (very reliable, free)
                if (!locationData) {
                    locationData = await fetchFromCloudFlare()
                }

                if (locationData) {
                    // Cache the location for 24 hours
                    cacheLocation(locationData)
                    setLocation({
                        ...locationData,
                        isLoading: false
                    })
                } else {
                    // If all APIs fail, use default
                    setLocation({
                        countryCode: 'US',
                        countryName: 'United States',
                        isLoading: false,
                        error: 'Could not detect location'
                    })
                }
            } catch (error) {
                console.error('Error detecting location:', error)
                setLocation({
                    countryCode: 'US',
                    countryName: 'United States',
                    isLoading: false,
                    error: error instanceof Error ? error.message : 'Unknown error'
                })
            }
        }

        detectLocation()
    }, [])

    return location
}

/**
 * Fetches location from ipapi.co
 */
async function fetchFromIpApi(): Promise<Omit<UserLocation, 'isLoading'> | null> {
    try {
        const response = await fetch('https://ipapi.co/json/', {
            headers: {
                'User-Agent': 'myworldesk.com'
            }
        })

        if (!response.ok) return null

        const data = await response.json()

        if (data.country_code) {
            return {
                countryCode: data.country_code,
                countryName: data.country_name,
                city: data.city
            }
        }
    } catch (error) {
        console.warn('ipapi.co failed:', error)
    }
    return null
}

/**
 * Fetches location from ip-api.com
 */
async function fetchFromIpApiCom(): Promise<Omit<UserLocation, 'isLoading'> | null> {
    try {
        const response = await fetch('http://ip-api.com/json/')

        if (!response.ok) return null

        const data = await response.json()

        if (data.countryCode) {
            return {
                countryCode: data.countryCode,
                countryName: data.country,
                city: data.city
            }
        }
    } catch (error) {
        console.warn('ip-api.com failed:', error)
    }
    return null
}

/**
 * Fetches location from CloudFlare trace
 */
async function fetchFromCloudFlare(): Promise<Omit<UserLocation, 'isLoading'> | null> {
    try {
        const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace')

        if (!response.ok) return null

        const data = await response.text()
        const lines = data.split('\n')
        const locLine = lines.find((line) => line.startsWith('loc='))

        if (locLine) {
            const countryCode = locLine.split('=')[1]
            return {
                countryCode,
                countryName: '' // CloudFlare doesn't provide country name
            }
        }
    } catch (error) {
        console.warn('CloudFlare trace failed:', error)
    }
    return null
}

/**
 * Caches location in localStorage for 24 hours
 */
function cacheLocation(location: Omit<UserLocation, 'isLoading'>) {
    try {
        const cacheData = {
            location,
            timestamp: Date.now()
        }
        localStorage.setItem('user_location', JSON.stringify(cacheData))
    } catch {
        // Ignore localStorage errors
    }
}

/**
 * Gets cached location if it's less than 24 hours old
 */
function getCachedLocation(): Omit<UserLocation, 'isLoading'> | null {
    try {
        const cached = localStorage.getItem('user_location')
        if (!cached) return null

        const { location, timestamp } = JSON.parse(cached)
        const age = Date.now() - timestamp
        const maxAge = 24 * 60 * 60 * 1000 // 24 hours

        if (age < maxAge) {
            return location
        }

        // Clear expired cache
        localStorage.removeItem('user_location')
    } catch {
        // Ignore localStorage errors
    }
    return null
}
